/**
 * SearchBar - GPS search input with global search via OpenStreetMap
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigationStore, DEMO_LOCATIONS, type SearchResult } from '@/stores/useNavigationStore';
import { useLocationStore } from '@/stores';
import { searchPlaces } from '@/services/api/geocodingApi';
import { GlassPanel } from '@/components/common';
import { cn } from '@/utils/cn';

interface SearchBarProps {
  className?: string;
}

export function SearchBar({ className }: SearchBarProps) {
  const [showResults, setShowResults] = useState(false);
  const [recentSearches, setRecentSearches] = useState<SearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentPosition = useLocationStore((state) => state.currentPosition);

  const searchQuery = useNavigationStore((state) => state.searchQuery);
  const searchResults = useNavigationStore((state) => state.searchResults);
  const isSearching = useNavigationStore((state) => state.isSearching);
  const isNavigating = useNavigationStore((state) => state.isNavigating);
  const setSearchQuery = useNavigationStore((state) => state.setSearchQuery);
  const setSearchResults = useNavigationStore((state) => state.setSearchResults);
  const setIsSearching = useNavigationStore((state) => state.setIsSearching);
  const selectDestination = useNavigationStore((state) => state.selectDestination);
  const clearSearch = useNavigationStore((state) => state.clearSearch);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('blurp_recent_searches');
      if (saved) {
        setRecentSearches(JSON.parse(saved).slice(0, 5));
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Handle search with global geocoding
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    // Debounce search
    const timer = setTimeout(async () => {
      try {
        // Search globally using OpenStreetMap Nominatim
        const results = await searchPlaces(searchQuery, {
          limit: 8,
          userLocation: currentPosition || undefined,
        });

        // If no results from API, fall back to demo locations
        if (results.length === 0) {
          const query = searchQuery.toLowerCase();
          const demoResults = DEMO_LOCATIONS.filter(
            (loc) =>
              loc.name.toLowerCase().includes(query) ||
              loc.address.toLowerCase().includes(query) ||
              loc.category?.toLowerCase().includes(query)
          );
          setSearchResults(demoResults);
        } else {
          setSearchResults(results);
        }
      } catch (error) {
        console.error('Search failed:', error);
        // Fall back to demo locations on error
        const query = searchQuery.toLowerCase();
        const demoResults = DEMO_LOCATIONS.filter(
          (loc) =>
            loc.name.toLowerCase().includes(query) ||
            loc.address.toLowerCase().includes(query)
        );
        setSearchResults(demoResults);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery, currentPosition, setSearchResults, setIsSearching]);

  // Close results on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback((result: SearchResult) => {
    selectDestination(result);
    setSearchQuery(result.name);
    setShowResults(false);

    // Save to recent searches
    setRecentSearches((prev) => {
      const updated = [result, ...prev.filter((r) => r.id !== result.id)].slice(0, 5);
      try {
        localStorage.setItem('blurp_recent_searches', JSON.stringify(updated));
      } catch {
        // Ignore localStorage errors
      }
      return updated;
    });
  }, [selectDestination, setSearchQuery]);

  const handleClear = () => {
    clearSearch();
    inputRef.current?.focus();
  };

  if (isNavigating) {
    return null; // Hide search when navigating
  }

  return (
    <div ref={containerRef} className={cn('relative w-full max-w-md', className)}>
      <GlassPanel padding="none" className="overflow-visible">
        <div className="flex items-center gap-2 px-3 py-2.5">
          <SearchIcon className="w-5 h-5 text-hud-primary flex-shrink-0" />

          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setShowResults(true)}
            placeholder="Where to?"
            className={cn(
              'flex-1 bg-transparent outline-none',
              'text-sm text-text-primary placeholder:text-text-muted',
              'font-body'
            )}
          />

          {searchQuery && (
            <button
              onClick={handleClear}
              className="p-1 text-text-secondary hover:text-hud-primary transition-colors"
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          )}

          {isSearching && (
            <div className="w-4 h-4 border-2 border-hud-primary border-t-transparent rounded-full animate-spin" />
          )}

          {/* Voice search button (placeholder) */}
          <button
            className="p-1 text-text-secondary hover:text-hud-primary transition-colors"
            title="Voice search (coming soon)"
          >
            <MicIcon className="w-4 h-4" />
          </button>
        </div>
      </GlassPanel>

      {/* Search Results Dropdown */}
      <AnimatePresence>
        {showResults && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-2 z-50"
          >
            <GlassPanel padding="none" className="max-h-[400px] overflow-y-auto">

              {/* Search results */}
              {searchResults.length > 0 ? (
                <ul className="divide-y divide-glass-border">
                  {searchResults.map((result) => (
                    <SearchResultItem
                      key={result.id}
                      result={result}
                      onSelect={handleSelect}
                    />
                  ))}
                </ul>
              ) : searchQuery && !isSearching ? (
                <div className="p-4 text-center text-sm text-text-secondary">
                  No results found for "{searchQuery}"
                  <div className="text-xs text-text-muted mt-1">
                    Try searching for a city, address, or place name
                  </div>
                </div>
              ) : null}

              {/* Recent searches / Suggestions */}
              {!searchQuery && (
                <div className="p-3">
                  {recentSearches.length > 0 && (
                    <>
                      <div className="text-xs text-text-muted uppercase tracking-wider mb-2 flex items-center gap-2">
                        <ClockIcon className="w-3 h-3" />
                        Recent
                      </div>
                      <ul className="divide-y divide-glass-border mb-4">
                        {recentSearches.map((result) => (
                          <SearchResultItem
                            key={result.id}
                            result={result}
                            onSelect={handleSelect}
                            isRecent
                          />
                        ))}
                      </ul>
                    </>
                  )}

                  <div className="text-xs text-text-muted uppercase tracking-wider mb-2 flex items-center gap-2">
                    <SparkleIcon className="w-3 h-3" />
                    Suggested for Clean Air
                  </div>
                  <ul className="divide-y divide-glass-border">
                    {DEMO_LOCATIONS.filter((l) => l.category === 'Park').slice(0, 3).map((result) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        onSelect={handleSelect}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </GlassPanel>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


interface SearchResultItemProps {
  result: SearchResult;
  onSelect: (result: SearchResult) => void;
  isRecent?: boolean;
}

function SearchResultItem({ result, onSelect, isRecent }: SearchResultItemProps) {
  return (
    <li>
      <button
        onClick={() => onSelect(result)}
        className={cn(
          'w-full px-3 py-2.5 text-left',
          'hover:bg-hud-primary/10 transition-colors',
          'flex items-start gap-3'
        )}
      >
        <div className="mt-0.5">
          {isRecent ? (
            <ClockIcon className="w-4 h-4 text-text-secondary" />
          ) : (
            <LocationPinIcon className="w-4 h-4 text-hud-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-text-primary truncate">
            {result.name}
          </div>
          <div className="text-xs text-text-secondary truncate">
            {result.address}
          </div>
          {result.distance !== undefined && (
            <div className="text-xs text-hud-primary mt-0.5">
              {formatDistance(result.distance)} away
            </div>
          )}
        </div>
        {result.category && (
          <span className="text-[10px] px-1.5 py-0.5 bg-hud-primary/20 text-hud-primary rounded font-hud uppercase">
            {result.category}
          </span>
        )}
      </button>
    </li>
  );
}

function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${meters}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}

// Icons
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3" />
    </svg>
  );
}

function LocationPinIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
    </svg>
  );
}


function ClockIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12,6 12,12 16,14" />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5L12 2z" />
    </svg>
  );
}

export default SearchBar;
